import { MASTERY_MAX_POINTS } from "@riseafrica/scoring";
import { useEffect } from "react";
import { shareWhatsApp } from "../lib/share";
import { useApp } from "../state/AppState";
import type { Volet3 as Volet3Result } from "../state/AppState";
import { Offers } from "../components/Offers";
import { Fil } from "../components/Fil";
import { RappelAgenda } from "../components/RappelAgenda";
import { SummaryCard } from "./Home";

/** Volet 03 — niveau de maîtrise et attestation. */
export function Result3({ t3 }: { t3: Volet3Result }) {
  const { goTo, metier, t2, registerShare, say } = useApp();

  useEffect(() => {
    say(`${t3.level.libelle} — ${t3.points} points sur ${MASTERY_MAX_POINTS}`);
  }, [say, t3.level.libelle, t3.points]);

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={3} fait />
        <div className="verdict">
          <div className="kicker">Niveau de maîtrise</div>
          <div className="score">
            {t3.points}
            <span>/{MASTERY_MAX_POINTS}</span>
          </div>
          <div className="gradient-label">{t3.level.libelle}</div>
          <p>{t3.level.texte}</p>
          {/*
            Ce que la redondance a servi à voir.

            Chaque axe est interrogé deux fois, dont une par une question témoin
            qui l'aborde autrement. Quand les deux réponses se contredisent sur
            plusieurs axes, le total reste ce qu'il est — on ne retire aucun
            point, ce serait punir une nuance que nos questions n'ont pas su
            capter — mais la confiance qu'on peut lui accorder baisse, et le
            produit le dit. C'est la seule raison d'avoir doublé le
            questionnaire : sans cet aveu possible, la redondance ne servirait à
            rien.
          */}
          {!t3.fiable && (
            <p className="fine">
              Sur {t3.contradictions} axes, vos deux réponses disent des choses opposées. Le niveau
              reste calculé tel quel, mais lisez-le comme un ordre de grandeur : c'est souvent le
              signe qu'on a répondu à l'une comme on voudrait faire, et à l'autre comme on fait.
            </p>
          )}
        </div>

        {/*
          Ce qui a bougé depuis la fois précédente.

          Placé **avant** l'attestation : quelqu'un qui revient est venu pour
          cela, pas pour relire son niveau. Un écart nul ou négatif s'affiche
          aussi — une mesure qui ne sait annoncer que les progrès n'est plus une
          mesure, c'est un encouragement, et l'attestation perdrait ce qui lui
          donne sa valeur auprès d'un tiers.
        */}
        {t3.progression !== null && (
          <div className="progres">
            <div className="k">Depuis votre attestation {t3.progression.precedent}</div>
            <div className="progres-ecart">
              {t3.progression.ecart > 0 ? "+" : ""}
              {t3.progression.ecart} point{Math.abs(t3.progression.ecart) > 1 ? "s" : ""}
              <small>en {t3.progression.jours} jours</small>
            </div>
            <p>
              {t3.progression.niveauAvant === t3.progression.niveauApres
                ? `Vous restez au niveau ${t3.progression.niveauApres}.`
                : `Vous passez de ${t3.progression.niveauAvant} à ${t3.progression.niveauApres}.`}{" "}
              {t3.progression.ecart > 0
                ? "Ce qui a changé tient à votre pratique, pas au barème : il n'a pas bougé entre les deux mesures."
                : t3.progression.ecart === 0
                  ? "Rien n'a bougé. C'est une information, pas un échec — les huit axes sont exigeants."
                  : "Vous avez reculé sur au moins un axe. Le barème n'a pas changé : c'est la pratique qui a glissé."}
            </p>
          </div>
        )}

        <div className="attest">
          <div className="k">Attestation de niveau · STRATERA</div>
          <div className="lvl">{t3.level.libelle}</div>
          <div className="k">{metier ?? "Profil professionnel"}</div>
          <div className="who">{t3.nom}</div>
          <div className="k">Délivrée le {t3.date}</div>
          {/*
            Le numéro vient du serveur, qui l'a enregistré — et il est
            désormais vérifiable pour de bon.

            Il était auparavant fabriqué par le navigateur et n'existait nulle
            part : la page de contrôle refusait jusqu'à son format. On promettait
            une vérification qui répondait « ce numéro n'a pas le format d'une
            attestation », sur un numéro que le produit venait d'écrire.

            Émission échouée : on n'écrit **aucun** numéro et on le dit. Une
            attestation sans numéro est une déception ; un numéro qui ne se
            vérifie pas est un mensonge, et c'est un tiers qui le découvre.
          */}
          {t3.serial === null ? (
            <div className="serial">
              Numéro indisponible — l'enregistrement n'a pas abouti. Le niveau ci-dessus reste
              juste ; refaites le volet pour obtenir un numéro vérifiable.
            </div>
          ) : (
            <div className="serial">
              {t3.serial}
              <br />
              <a className="lien-cible" href={`/attestation/${t3.serial}`}>
                vérifier cette attestation
              </a>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn btn-wa"
          style={{ marginTop: "12px" }}
          onClick={() => {
            registerShare();
            shareWhatsApp(
              `Je viens d'obtenir mon attestation STRATERA — ${t3.level.libelle} en usage professionnel de l'IA. Évalue ton niveau :`,
            );
          }}
        >
          Partager mon attestation
        </button>
        <p className="hint">S'ouvre dans WhatsApp. Pour un autre réseau, copiez le lien « vérifier cette attestation » ci-dessus.</p>

        <div className="block">
          <h2>Votre profil complet</h2>
          <p className="sub">Les trois volets réunis. C'est le point de départ du rapport.</p>
          <SummaryCard />
        </div>

        <Offers money={t2?.money ?? 0} />

        {/*
          Ce bloc promettait « la première automatisation, gratuitement » contre
          un numéro de téléphone, et son envoi ne faisait qu'incrémenter un
          compteur — le numéro ne partait nulle part, et aucune automatisation
          n'a jamais été envoyée à personne.

          Il était masqué tant que la messagerie est coupée. Cette garde suffit
          aujourd'hui et échoue demain : le jour où le compte WhatsApp Business
          s'ouvre — une des choses prévues au lancement — le bloc réapparaît, et
          il ment. Le même piège dormait sur le résultat du volet 1, avec le même
          alibi. Les deux partent.

          Ce qui reste à la place n'est pas rien : le partage, le téléchargement
          et le rappel d'agenda ci-dessous ramènent quelqu'un sans lui demander
          quoi que ce soit de nominatif — ce qui est la position du produit.
        */}

        <RappelAgenda
          proeminent
          titre="Re-mesurer ma maîtrise de l'IA"
          mesure={`Niveau précédent : ${t3.level.libelle}, ${t3.points} points sur ${MASTERY_MAX_POINTS}.`}
          pourquoi="La maîtrise se perd quand on ne s'en sert pas, et se gagne vite quand on s'y met. C'est le volet qui bouge le plus en trois mois, dans les deux sens."
          accroche="Votre niveau vaut trois mois"
          ecran="r3"
        />

        <button type="button" className="back" onClick={() => goTo("home")}>
          ← Revenir au diagnostic
        </button>
      </div>
    </section>
  );
}
